import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { formatCurrency } from 'frontend/helpers/formatPrice';
import { useColors } from 'frontend/hooks/useColors';
import { useMemo } from 'react';

const WarningsModal = ({ open, onClose, warnings = [], onAccept, handleBackToResults }) => {
  const { background, text } = useColors();

  const backToResultsUrl = useMemo(() => handleBackToResults(), [handleBackToResults]);

  const getPriceChangeInfo = (warning) => {
    if (warning.id !== 'CHANGE_PRICE' || !warning.extensions) return null;

    const { originalAmount, currentAmount, currency } = warning.extensions;

    return {
      originalAmount,
      currentAmount,
      currency,
      difference: currentAmount - originalAmount,
      isIncrease: currentAmount > originalAmount,
    };
  };

  const handleCloseModal = () => {
    onClose();
    setTimeout(() => {
      window.location.href = backToResultsUrl;
    }, 300);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: (theme) => theme.palette.warning.light,
          color: (theme) => theme.palette.warning.contrastText,
        }}
      >
        <WarningAmberIcon color="warning" sx={{ color: '#fff' }} />
        <Typography variant="h6" component="div">
          {warnings[0]?.extensions?.title || 'Aviso importante'}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ py: 3, paddingTop: '24px !important' }}>
        {warnings.map((warning, index) => {
          const priceChange = getPriceChangeInfo(warning);

          return (
            <Box key={index} sx={{ mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {warning.short_name}
              </Typography>

              <Typography variant="body1" paragraph>
                {warning.description}
              </Typography>

              {priceChange && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 1 }}>
                    <InfoOutlinedIcon color="info" />
                    <Typography variant="body2" color="text.secondary">
                      Detalle del cambio de precio:
                    </Typography>
                  </Stack>

                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    Precio original:{' '}
                    {formatCurrency(priceChange.originalAmount, priceChange.currency)}
                  </Typography>

                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    Precio actual: {formatCurrency(priceChange.currentAmount, priceChange.currency)}
                  </Typography>

                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    color={priceChange.isIncrease ? 'error.main' : 'success.main'}
                  >
                    Diferencia: {priceChange.isIncrease ? '+' : ''}
                    {formatCurrency(priceChange.difference, priceChange.currency)}
                  </Typography>
                </Box>
              )}
            </Box>
          );
        })}

        <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
          Para continuar con la reserva debe aceptar el cambio de precio, o volver a los resultados
          para seleccionar otra opción.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<ArrowBackIosIcon />}
          onClick={handleCloseModal}
          sx={{
            borderColor: background === 'dark' ? text.primary : text.secondary,
            color: background === 'dark' ? text.primary : text.secondary,
            '&:hover': {
              borderColor: background === 'dark' ? text.primary : text.secondary,
              backgroundColor: 'transparent',
            },
          }}
        >
          Volver a resultados
        </Button>

        <Button
          variant="contained"
          onClick={onAccept}
          sx={{
            backgroundColor: background,
            color: text,
            '&:hover': {
              backgroundColor: background,
              color: text,
              opacity: 0.7,
            },
          }}
        >
          Aceptar cambio
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WarningsModal;
